import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { Haptics, ImpactStyle } from '@capacitor/haptics';

/** Drag distance (px) past which releasing dismisses the sheet. */
const DISMISS_THRESHOLD = 100;
/** Downward flick speed (px/ms) that dismisses regardless of distance. */
const FLICK_VELOCITY = 0.5;
/** How long the slide-down exit transition runs before `onDismiss` fires. */
export const DISMISS_ANIMATION_MS = 250;
/** Chakra `md` breakpoint — the sheet behaves as a centered dialog above this. */
const MOBILE_MAX_WIDTH = 767;

export interface SwipeToDismissState {
  /** Attach to the sheet element that should follow the finger. */
  sheetRef: RefObject<HTMLDivElement>;
  /** Attach to the sheet's inner scroll container (drag only engages at its top). */
  scrollRef: RefObject<HTMLDivElement>;
  /** Current downward offset (px) while dragging. */
  dragY: number;
  /** True while the finger is actively dragging (transitions should be off). */
  dragging: boolean;
  /** True while the slide-down exit animation is playing. */
  dismissing: boolean;
  /** Close with the slide-down animation (falls back to instant on desktop). */
  requestDismiss: () => void;
}

/**
 * Swipe-down-to-dismiss for bottom-sheet style modals, following the touch
 * conventions of `usePullToRefresh`. Mobile layout only (≤767px): on wider
 * viewports the state stays inert and `requestDismiss` closes immediately.
 *
 * A downward drag takes over only when the inner scroller is at the top, so
 * scrolling content inside the sheet keeps working; dragging the header always
 * engages.
 *
 * `active` should mirror the modal's `visible` condition — the sheet element
 * only exists in the DOM while open, so the listeners (re)attach and the drag
 * state resets on every open.
 */
export function useSwipeToDismiss(onDismiss: () => void, active: boolean): SwipeToDismissState {
  const sheetRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const [dragY, setDragY] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  // Refs mirror state for use inside the (once-attached) native listeners.
  const dragYRef = useRef(0);
  const draggingRef = useRef(false);
  const dismissingRef = useRef(false);
  const startYRef = useRef<number | null>(null);
  const startedOutsideScrollRef = useRef(false);
  const lastMoveRef = useRef<{ y: number; t: number } | null>(null);
  const velocityRef = useRef(0);
  const hapticFiredRef = useRef(false);
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  const setDrag = (value: number) => {
    dragYRef.current = value;
    setDragY(value);
  };

  const isMobile = () =>
    typeof window !== 'undefined' && window.innerWidth <= MOBILE_MAX_WIDTH;

  const beginDismiss = useCallback(() => {
    if (dismissingRef.current) return;
    dismissingRef.current = true;
    setDismissing(true);
    dismissTimerRef.current = setTimeout(() => {
      onDismissRef.current();
    }, DISMISS_ANIMATION_MS);
  }, []);

  const requestDismiss = useCallback(() => {
    if (!isMobile()) {
      onDismissRef.current();
      return;
    }
    beginDismiss();
  }, [beginDismiss]);

  // Reset drag/dismiss state whenever the sheet (re)opens.
  useEffect(() => {
    if (!active) return;
    dismissingRef.current = false;
    draggingRef.current = false;
    startYRef.current = null;
    setDismissing(false);
    setDragging(false);
    setDrag(0);
    if (dismissTimerRef.current) {
      clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
  }, [active]);

  useEffect(() => {
    const el = sheetRef.current;
    if (!el || !active) return;

    const onTouchStart = (e: TouchEvent) => {
      if (dismissingRef.current || !isMobile()) return;
      startYRef.current = e.touches[0].clientY;
      // Touches on the header/handle (outside the scroller) always drag the sheet.
      startedOutsideScrollRef.current = !(
        scrollRef.current?.contains(e.target as Node) ?? false
      );
      lastMoveRef.current = { y: e.touches[0].clientY, t: performance.now() };
      velocityRef.current = 0;
      hapticFiredRef.current = false;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (dismissingRef.current || startYRef.current === null) return;

      const y = e.touches[0].clientY;
      const delta = y - startYRef.current;

      // Track instantaneous downward velocity for flick detection.
      const last = lastMoveRef.current;
      const now = performance.now();
      if (last && now > last.t) {
        velocityRef.current = (y - last.y) / (now - last.t);
      }
      lastMoveRef.current = { y, t: now };

      if (!draggingRef.current) {
        // Engage only on a downward pull while the content is scrolled to top
        // (or when the touch began on the header/handle).
        const scrollTop = scrollRef.current?.scrollTop ?? 0;
        if (delta <= 0 || (scrollTop > 0 && !startedOutsideScrollRef.current)) {
          // Re-anchor so a later top-of-scroll pull measures from here, and an
          // upward drag doesn't bank negative distance.
          startYRef.current = y;
          return;
        }
        draggingRef.current = true;
        setDragging(true);
      }

      e.preventDefault();
      const next = Math.max(0, delta);
      setDrag(next);

      if (next >= DISMISS_THRESHOLD && !hapticFiredRef.current) {
        hapticFiredRef.current = true;
        void Haptics.impact({ style: ImpactStyle.Light }).catch(() => {});
      } else if (next < DISMISS_THRESHOLD) {
        hapticFiredRef.current = false;
      }
    };

    const onTouchEnd = () => {
      const wasDragging = draggingRef.current;
      draggingRef.current = false;
      setDragging(false);
      startYRef.current = null;
      lastMoveRef.current = null;
      if (dismissingRef.current || !wasDragging) return;

      if (dragYRef.current >= DISMISS_THRESHOLD || velocityRef.current >= FLICK_VELOCITY) {
        beginDismiss();
      } else {
        setDrag(0);
      }
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    // Non-passive so an engaged drag can preventDefault the inner scroll.
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    el.addEventListener('touchcancel', onTouchEnd, { passive: true });

    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [active, beginDismiss]);

  useEffect(
    () => () => {
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    },
    [],
  );

  return { sheetRef, scrollRef, dragY, dragging, dismissing, requestDismiss };
}
